import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Alert, 
  Chip,
  Divider
} from '@mui/material';
import {
  Article as ArticleIcon,
  Security as SecurityIcon,
  Payment as PaymentIcon,
  Hardware as HardwareIcon,
  Gavel as GavelIcon
} from '@mui/icons-material';
import Navigation from '../components/Navigation';

const Tos: React.FC = () => {

  const sections = [
    { id: 'acceptance', title: '1. Acceptance of Terms', icon: <GavelIcon /> },
    { id: 'service', title: '2. Description of Service', icon: <ArticleIcon /> },
    { id: 'third-party', title: '3. Third-Party Service Dependencies', icon: <SecurityIcon /> },
    { id: 'account', title: '4. Account Registration and Security', icon: <SecurityIcon /> },
    { id: 'billing', title: '5. Subscription Terms and Billing', icon: <PaymentIcon /> },
    { id: 'hardware', title: '6. Display Hardware and Device Management', icon: <HardwareIcon /> },
    { id: 'acceptable-use', title: '7. Acceptable Use', icon: <GavelIcon /> },
    { id: 'content', title: '8. Content Guidelines and Responsibilities', icon: <ArticleIcon /> },
    { id: 'privacy', title: '9. Data Privacy and California Consumer Rights', icon: <SecurityIcon /> },
    { id: 'ip', title: '10. Intellectual Property', icon: <GavelIcon /> },
    { id: 'availability', title: '11. Service Availability and Performance', icon: <ArticleIcon /> },
    { id: 'termination', title: '12. Account Termination and Data Retention', icon: <SecurityIcon /> },
    { id: 'liability', title: '13. Limitation of Liability', icon: <GavelIcon /> },
    { id: 'indemnification', title: '14. Indemnification', icon: <GavelIcon /> },
    { id: 'dispute', title: '15. Dispute Resolution and Governing Law', icon: <GavelIcon /> },
    { id: 'changes', title: '16. Changes to Terms', icon: <ArticleIcon /> },
    { id: 'general', title: '17. General Provisions', icon: <GavelIcon /> },
    { id: 'contact', title: '18. Contact Information', icon: <ArticleIcon /> }
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50' }}>
      {/* Hero Section */}
      <Box sx={{ 
        color: 'white', 
        py: 8,
        background: 'linear-gradient(135deg, #4caf50 0%, #6fbf73 30%, #357a38 60%, #4caf50 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background Pattern */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 80%, rgba(255,255,255,0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.05) 0%, transparent 50%)',
          pointerEvents: 'none'
        }} />
        
        {/* Navigation */}
        <Navigation />
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
              Terms and Conditions
            </Typography>
            
            <Typography variant="h5" component="h2" sx={{ opacity: 0.9, maxWidth: 800, mx: 'auto', lineHeight: 1.4 }}>
              Legal framework for your FamilyCast experience
            </Typography>
          </Box>
        </Container>
      </Box>
      
      {/* Alerts Section */}
      <Container maxWidth="lg" sx={{ pt: 4, pb: 2 }}>
        <Alert severity="warning" sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}>
          <Typography variant="body2" component="div">
            <strong>Version:</strong> 2.0 (Pre-Alpha Draft) | <strong>Status:</strong> DRAFT - NOT YET ACTIVE
          </Typography>
        </Alert>

        <Alert severity="info" sx={{ mb: 4, maxWidth: 700, mx: 'auto' }}>
          <Typography variant="body2">
            <strong>Pre-Alpha Notice:</strong> FamilyCast is currently in pre-alpha development. Features, pricing, and terms may change significantly before general availability.
          </Typography>
        </Alert>
      </Container>
      
      <Container maxWidth="lg" sx={{ py: 6 }}>
        {/* Table of Contents */}
        <Paper sx={{ p: 6, mb: 6, borderRadius: 2, boxShadow: 2 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#4caf50', mb: 3 }}>
            Table of Contents
          </Typography>
          <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 4, color: 'text.secondary' }}>
            Navigate through our comprehensive Terms and Conditions covering all aspects of FamilyCast usage, including hardware management, billing, and privacy rights.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {sections.map((section) => (
              <Chip
                key={section.id}
                label={section.title}
                variant="outlined"
                size="small"
                onClick={() => document.getElementById(section.id)?.scrollIntoView({ behavior: 'smooth' })}
                sx={{ 
                  cursor: 'pointer',
                  borderColor: '#4caf50',
                  color: '#4caf50',
                  '&:hover': { 
                    bgcolor: '#4caf50', 
                    color: 'white',
                    transform: 'translateY(-1px)',
                    transition: 'all 0.2s ease-in-out'
                  }
                }}
              />
            ))}
          </Box>
        </Paper>

        {/* Main Content */}
        <Paper sx={{ p: 6, borderRadius: 2, boxShadow: 2 }}>
          {/* Section 1: Acceptance of Terms */}
          <Box id="acceptance" sx={{ mb: 6 }}>
            <Typography variant="h4" gutterBottom sx={{ color: '#4caf50', fontWeight: 600 }}>
              1. Acceptance of Terms
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 3 }}>
              By accessing, browsing, or using FamilyCast ("Service"), you acknowledge that you have read, understood, and agree to be bound by these Terms and our Privacy Policy.
            </Typography>
            <Typography variant="body1" component="ul" sx={{ fontSize: '1.0rem', lineHeight: 1.6, pl: 3 }}>
              <li><strong>Legal Capacity:</strong> You must be at least 18 years old or have reached the age of majority in your jurisdiction</li>
              <li><strong>Business Use:</strong> If using the Service on behalf of a business, you represent that you have authority to bind that entity</li>
              <li><strong>Modifications:</strong> We reserve the right to modify these Terms at any time with appropriate notice</li>
            </Typography>
          </Box>

          <Divider sx={{ my: 6 }} />

          {/* Section 2: Description of Service */}
          <Box id="service" sx={{ mb: 6 }}>
            <Typography variant="h4" gutterBottom sx={{ color: '#4caf50', fontWeight: 600 }}>
              2. Description of Service
            </Typography>
            
            <Typography variant="h6" gutterBottom sx={{ mt: 4, color: '#4caf50' }}>
              2.1 Core Platform
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 3 }}>
              FamilyCast provides a comprehensive digital signage and content management platform that enables users to:
            </Typography>
            <Typography variant="body1" component="ul" sx={{ fontSize: '1.0rem', lineHeight: 1.6, pl: 3, mb: 4 }}>
              <li>Create and manage digital displays and signage content</li>
              <li>Design custom layouts and schedules for content presentation</li>
              <li>Integrate with third-party services and data sources</li>
              <li>Monitor and control displays remotely</li>
              <li>Manage user accounts, permissions, and organizational structures</li>
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 4, color: '#4caf50' }}>
              2.2 Third-Party Integrations
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 3 }}>
              FamilyCast integrates with various third-party services to enhance functionality:
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Alert severity="info" variant="outlined" sx={{ mb: 2, borderColor: '#4caf50' }}>
                <Typography variant="body1" sx={{ color: '#4caf50' }}>
                  <strong>Payment Processing:</strong> Stripe for subscription billing and payment processing
                </Typography>
              </Alert>
              <Alert severity="info" variant="outlined" sx={{ mb: 2, borderColor: '#4caf50' }}>
                <Typography variant="body1" sx={{ color: '#4caf50' }}>
                  <strong>Content Sources:</strong> Google Photos for image content, Canva for design templates
                </Typography>
              </Alert>
              <Alert severity="info" variant="outlined" sx={{ borderColor: '#4caf50' }}>
                <Typography variant="body1" sx={{ color: '#4caf50' }}>
                  <strong>Additional Services:</strong> Weather APIs, calendar integrations, and other data sources
                </Typography>
              </Alert>
            </Box>
          </Box>

          <Divider sx={{ my: 6 }} />

          {/* Section 5: Subscription Terms and Billing - Highlighting key section */}
          <Box id="billing" sx={{ mb: 6 }}>
            <Typography variant="h4" gutterBottom sx={{ color: '#4caf50', fontWeight: 600 }}>
              5. Subscription Terms and Billing
            </Typography>
            
            <Typography variant="h6" gutterBottom sx={{ mt: 4, color: '#4caf50' }}>
              5.2 Free Trial
            </Typography>
            <Alert severity="success" sx={{ mb: 3, borderColor: '#4caf50', bgcolor: 'rgba(139, 69, 19, 0.05)' }}>
              <Typography variant="body1" sx={{ color: '#4caf50' }}>
                <strong>🎉 30-Day Free Trial Available!</strong> New users are eligible for a 30-day free trial of our Standard Quota Profile.
              </Typography>
            </Alert>
            <Typography variant="body1" component="ul" sx={{ fontSize: '1.0rem', lineHeight: 1.6, pl: 3 }}>
              <li><strong>Trial Period:</strong> 30 days from account creation</li>
              <li><strong>Full Access:</strong> Complete access to Standard tier features during trial</li>
              <li><strong>No Payment Required:</strong> Credit card not required for trial activation</li>
              <li><strong>Trial Conversion:</strong> Must subscribe to continue service after trial expires</li>
              <li><strong>Data Retention:</strong> Account data preserved for 30 days after trial expiration</li>
              <li><strong>One Per Customer:</strong> Limited to one trial per user/organization</li>
            </Typography>
          </Box>

          <Divider sx={{ my: 6 }} />

          {/* Section 6: Hardware - New section */}
          <Box id="hardware" sx={{ mb: 6 }}>
            <Typography variant="h4" gutterBottom sx={{ color: '#4caf50', fontWeight: 600 }}>
              6. Display Hardware and Device Management
            </Typography>
            
            <Typography variant="h6" gutterBottom sx={{ mt: 4, color: '#4caf50' }}>
              6.1 Hardware Compatibility and Requirements
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 3 }}>
              FamilyCast supports various display hardware configurations. Users are responsible for:
            </Typography>
            <Typography variant="body1" component="ul" sx={{ fontSize: '1.0rem', lineHeight: 1.6, pl: 3, mb: 4 }}>
              <li>Ensuring hardware meets minimum system requirements</li>
              <li>Maintaining compatible web browsers and operating systems</li>
              <li>Providing stable internet connectivity for content delivery</li>
              <li>Hardware procurement, setup, and physical maintenance</li>
              <li>Compliance with local electrical and safety codes</li>
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 4, color: '#4caf50' }}>
              6.2 Display Device Limits and Monitoring
            </Typography>
            <Typography variant="body1" component="ul" sx={{ fontSize: '1.0rem', lineHeight: 1.6, pl: 3 }}>
              <li><strong>Subscription Limits:</strong> Each subscription tier includes specific display device limits</li>
              <li><strong>Usage Monitoring:</strong> We monitor active displays to ensure compliance with subscription terms</li>
              <li><strong>Overage Policy:</strong> Exceeding display limits may result in service suspension or additional charges</li>
              <li><strong>Workaround Prohibition:</strong> Using technical workarounds to exceed display limits is prohibited</li>
            </Typography>
          </Box>

          <Divider sx={{ my: 6 }} />

          {/* Section 9: California Rights - Important section */}
          <Box id="privacy" sx={{ mb: 6 }}>
            <Typography variant="h4" gutterBottom sx={{ color: '#4caf50', fontWeight: 600 }}>
              9. Data Privacy and California Consumer Rights
            </Typography>
            
            <Typography variant="h6" gutterBottom sx={{ mt: 4, color: '#4caf50' }}>
              9.2 California Consumer Privacy Act (CCPA) Rights
            </Typography>
            <Alert severity="info" sx={{ mb: 3, borderColor: '#4caf50', bgcolor: 'rgba(139, 69, 19, 0.05)' }}>
              <Typography variant="body1" sx={{ color: '#4caf50' }}>
                <strong>California Residents:</strong> You have specific rights regarding your personal information under CCPA.
              </Typography>
            </Alert>
            <Typography variant="body1" component="ul" sx={{ fontSize: '1.0rem', lineHeight: 1.6, pl: 3, mb: 3 }}>
              <li><strong>Right to Know:</strong> Request disclosure of personal information collected</li>
              <li><strong>Right to Delete:</strong> Request deletion of personal information</li>
              <li><strong>Right to Opt-Out:</strong> Opt-out of sale of personal information</li>
              <li><strong>Right to Non-Discrimination:</strong> No discrimination for exercising CCPA rights</li>
            </Typography>
            <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.7, mt: 3 }}>
              To exercise these rights, contact us at <strong>privacy@familycast.app</strong> or through your account settings.
            </Typography>
          </Box>

          <Divider sx={{ my: 6 }} />

          {/* Condensed remaining sections */}
          <Box sx={{ mb: 6 }}>
            <Alert severity="info" sx={{ mb: 4, borderColor: '#4caf50', bgcolor: 'rgba(139, 69, 19, 0.05)' }}>
              <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.7, color: '#4caf50' }}>
                <strong>Additional Sections:</strong> This Terms of Service includes 18 comprehensive sections covering all aspects of service usage, including liability limitations, intellectual property rights, dispute resolution, and account termination procedures. The complete terms are legally binding and govern your use of FamilyCast.
              </Typography>
            </Alert>

            <Typography variant="h5" gutterBottom sx={{ color: '#4caf50', fontWeight: 600, mb: 3 }}>
              Key Additional Protections Include:
            </Typography>
            <Typography variant="body1" component="ul" sx={{ fontSize: '1.0rem', lineHeight: 1.6, pl: 3, mb: 3 }}>
              <li><strong>Limitation of Liability:</strong> Clear damage limitations and service disclaimers</li>
              <li><strong>Intellectual Property:</strong> Protection of FamilyCast IP and user content licensing</li>
              <li><strong>Dispute Resolution:</strong> Delaware jurisdiction with informal resolution requirements</li>
              <li><strong>Content Guidelines:</strong> Comprehensive prohibited content policies and DMCA procedures</li>
              <li><strong>Account Termination:</strong> Clear termination procedures and data retention policies</li>
            </Typography>
          </Box>

          <Divider sx={{ my: 6 }} />

          {/* Contact Information */}
          <Box id="contact" sx={{ mb: 6 }}>
            <Typography variant="h4" gutterBottom sx={{ color: '#4caf50', fontWeight: 600 }}>
              18. Contact Information
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 4 }}>
              Questions about these Terms should be directed to our specialized support teams:
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3 }}>
              <Alert severity="info" variant="outlined" sx={{ borderColor: '#4caf50', bgcolor: 'rgba(139, 69, 19, 0.05)' }}>
                <Typography variant="body1" sx={{ color: '#4caf50' }}>
                  <strong>General Support:</strong><br />support@familycast.app
                </Typography>
              </Alert>
              <Alert severity="info" variant="outlined" sx={{ borderColor: '#4caf50', bgcolor: 'rgba(139, 69, 19, 0.05)' }}>
                <Typography variant="body1" sx={{ color: '#4caf50' }}>
                  <strong>Privacy Matters:</strong><br />privacy@familycast.app
                </Typography>
              </Alert>
              <Alert severity="info" variant="outlined" sx={{ borderColor: '#4caf50', bgcolor: 'rgba(139, 69, 19, 0.05)' }}>
                <Typography variant="body1" sx={{ color: '#4caf50' }}>
                  <strong>Legal Issues:</strong><br />legal@familycast.app
                </Typography>
              </Alert>
              <Alert severity="info" variant="outlined" sx={{ borderColor: '#4caf50', bgcolor: 'rgba(139, 69, 19, 0.05)' }}>
                <Typography variant="body1" sx={{ color: '#4caf50' }}>
                  <strong>DMCA Agent:</strong><br />dmca@familycast.app
                </Typography>
              </Alert>
            </Box>
          </Box>

          {/* Final Acknowledgment */}
          <Alert severity="warning" sx={{ mt: 6, borderColor: '#4caf50', bgcolor: 'rgba(210, 105, 30, 0.05)' }}>
            <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.7, color: '#4caf50' }}>
              <strong>Acknowledgment:</strong> By using FamilyCast, you acknowledge that you have read and understood these Terms and Conditions and agree to be bound by them. If you do not agree to these Terms, you must not use our services.
            </Typography>
          </Alert>
        </Paper>
      </Container>
    </Box>
  );
};

export default Tos;